import clsx from 'clsx'
import {FC, ReactNode, useMemo, useState} from 'react'
import Select, {components, FormatOptionLabelMeta, MenuListProps, SingleValue} from 'react-select'
import {FieldHelpLabel} from '../../../../../../_metronic/helpers/components/FieldHelp'
import {InputHint} from '../../../../../../_metronic/helpers/components/InputHint'
import {
  findShipmentLocation,
  searchShipmentLocations,
  ShipmentLocation,
  ShipmentLocationKind,
} from '../core/shipmentLocationCodes'

// Keeps the open menu light; the 1,319 cities are reached by typing.
const MENU_LIMIT = 80

type Option = ShipmentLocation & {unknown?: boolean}

const copy = {
  country: {
    placeholder: 'نام یا کد کشور را جستجو کنید',
    noOptions: 'کشوری با این نام یا کد در جدول کدینگ سازمان نیست.',
    noun: 'کشور',
    unknown: (code: string) =>
      `کد «${code}» در جدول کدینگ کشورهای سامانه مودیان نیست؛ کشور را از فهرست انتخاب کنید.`,
  },
  city: {
    placeholder: 'نام یا کد شهر را جستجو کنید',
    noOptions: 'شهری با این نام یا کد در جدول کدینگ سازمان نیست.',
    noun: 'شهر',
    unknown: (code: string) =>
      `کد «${code}» در جدول کدینگ شهرهای سامانه مودیان نیست؛ شهر را از فهرست انتخاب کنید.`,
  },
}

type MenuNoteProps = {menuNote?: string}

const MenuListWithNote = (props: MenuListProps<Option, false>) => {
  const {menuNote} = props.selectProps as unknown as MenuNoteProps
  return (
    <components.MenuList {...props}>
      {props.children}
      {menuNote && <div className='px-4 py-2 text-gray-600 fs-7 border-top'>{menuNote}</div>}
    </components.MenuList>
  )
}

const formatOptionLabel = (option: Option, {context}: FormatOptionLabelMeta<Option>) =>
  context === 'value' ? (
    // Plain inline text: react-select clips a flex row inside the RTL value container.
    <span className={clsx({'text-danger': option.unknown})}>
      {option.name}{' '}
      <span className='text-gray-600 fs-7' dir='ltr'>
        ({option.code})
      </span>
    </span>
  ) : (
    <span className='d-flex align-items-center justify-content-between gap-3'>
      <span>{option.name}</span>
      <span className='text-gray-600 fs-7 text-nowrap' dir='ltr'>
        {option.alias ? `${option.alias} · ` : ''}
        {option.code}
      </span>
    </span>
  )

type Props = {
  kind: ShipmentLocationKind
  inputId: string
  label: ReactNode
  helpKey?: string
  value: string
  onChange: (code: string) => void
  isRequired?: boolean
  isDisabled?: boolean
  hint?: ReactNode
}

const ShipmentLocationSelect: FC<Props> = ({
  kind,
  inputId,
  label,
  helpKey,
  value,
  onChange,
  isRequired = false,
  isDisabled = false,
  hint,
}) => {
  const [query, setQuery] = useState('')
  const words = copy[kind]
  const code = (value || '').trim()
  const known = findShipmentLocation(kind, code)
  const selected: Option | null = !code
    ? null
    : known || {code, name: 'خارج از جدول سازمان', unknown: true}

  const {items, total} = useMemo(() => searchShipmentLocations(kind, query, MENU_LIMIT), [kind, query])
  const menuNote =
    total > items.length
      ? `${items.length.toLocaleString('fa-IR')} ${words.noun} از ${total.toLocaleString(
          'fa-IR'
        )} نمایش داده شد؛ برای یافتن بقیه، بخشی از نام یا کد را بنویسید.`
      : undefined
  const menuPortalTarget = typeof document !== 'undefined' ? document.body : undefined

  return (
    <>
      <label className='fw-bold fs-6 mb-2' htmlFor={inputId}>
        <FieldHelpLabel label={label} helpKey={helpKey} isRequired={isRequired} />{' '}
        {isRequired && <span title='ضروری' className='fw-bold fs-6 required'></span>}
      </label>
      <Select<Option, false>
        inputId={inputId}
        aria-invalid={selected?.unknown ? true : undefined}
        classNamePrefix='select'
        isRtl
        isClearable
        isDisabled={isDisabled}
        placeholder={words.placeholder}
        noOptionsMessage={() => words.noOptions}
        options={items}
        filterOption={null}
        onInputChange={(text) => {
          setQuery(text)
          return text
        }}
        value={selected}
        onChange={(option: SingleValue<Option>) => onChange(option ? option.code : '')}
        getOptionValue={(option) => option.code}
        getOptionLabel={(option) => `${option.name} ${option.code}`}
        formatOptionLabel={formatOptionLabel}
        components={{MenuList: MenuListWithNote}}
        {...({menuNote} as MenuNoteProps)}
        menuPosition='fixed'
        menuPortalTarget={menuPortalTarget}
        styles={{
          menuPortal: (base) => ({...base, zIndex: 10000}),
          menu: (base) => ({...base, zIndex: 10000}),
        }}
      />
      <InputHint hint={hint} error={selected?.unknown ? words.unknown(code) : undefined} />
    </>
  )
}

export {ShipmentLocationSelect}
